// ThirdPartyRiskPage.js
import React from 'react';
import styles from './ThirdPartyRiskPage.module.css';

const ThirdPartyRiskPage = () => {
  return (
    <div className={styles.thirdPartyRiskPage}>
      {/* Main Content Section */}
      <section className={styles.mainSection}>
        <div className={styles.container}>
          <div className={styles.contentGrid}>
            {/* Left Content */}
            <div className={styles.leftContent}>
              <h1 className={styles.pageTitle}>Third-Party Risk Management</h1>
              <p className={styles.pageDescription}>
                With increasing reliance on vendors and outsourced services, Virtual 
                IP helps you gain full control over third-party risk. Our Third-Party 
                Risk Management services evaluate, monitor, and enforce 
                cybersecurity standards across your external relationships—
                ensuring your extended ecosystem doesn't become your weakest 
                link.
              </p>
            </div>

            {/* Right Visual - placeholder for now */}
            <div className={styles.rightVisual}>
              <div className={styles.heroBackground}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Vendor Risk Assessments Section */}
      <section className={styles.vendorSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Vendor Risk Assessments
              </h2>
              <div className={styles.titleUnderline}></div>
              <p className={styles.sectionDescription}>
                We perform detailed evaluations of vendors' cybersecurity 
                practices, past breach history, data handling protocols, and 
                compliance with industry benchmarks. This allows you to work only 
                with trusted partners that uphold your organization's security 
                expectations.
              </p>
            </div>
            <div className={styles.sectionVisual}>
              <div className={styles.vendorAssessmentIllustration}>
                <div className={styles.dashboardTablet}>
                  <div className={styles.tabletBase}></div>
                  <div className={styles.tabletScreen}>
                    <div className={styles.dashboardContent}>
                      <div className={styles.pieChart1}>
                        <div className={styles.pieSegment1}></div>
                        <div className={styles.pieSegment2}></div>
                        <div className={styles.pieSegment3}></div>
                        <div className={styles.pieCenter}>4</div>
                      </div>
                      <div className={styles.pieChart2}>
                        <div className={styles.pieSegment4}></div>
                        <div className={styles.pieSegment5}></div>
                        <div className={styles.pieCenter}>8</div>
                      </div>
                      <div className={styles.pieChart3}>
                        <div className={styles.pieSegment6}></div>
                        <div className={styles.pieSegment7}></div>
                        <div className={styles.pieCenter}>21</div>
                      </div>
                      <div className={styles.barChart}>
                        <div className={styles.bar1}></div>
                        <div className={styles.bar2}></div>
                        <div className={styles.bar3}></div>
                        <div className={styles.bar4}></div>
                        <div className={styles.bar5}></div>
                      </div>
                      <div className={styles.dataPoints}>
                        <div className={styles.dataPoint}></div>
                        <div className={styles.dataPoint}></div>
                        <div className={styles.dataPoint}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Continuous Monitoring Section */}
      <section className={styles.monitoringSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionVisual}>
              <div className={styles.monitoringIllustration}>
                <div className={styles.monitoringBackground}>
                  <div className={styles.dashboardScreens}>
                    <div className={styles.screen1}>
                      <div className={styles.screenFrame}></div>
                      <div className={styles.screenContent}>
                        <div className={styles.checkmark}>✓</div>
                      </div>
                    </div>
                    <div className={styles.screen2}>
                      <div className={styles.screenFrame}></div>
                      <div className={styles.screenContent}>
                        <div className={styles.chartLines}></div>
                      </div>
                    </div>
                    <div className={styles.screen3}>
                      <div className={styles.screenFrame}></div>
                      <div className={styles.screenContent}>
                        <div className={styles.pieDisplay}></div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.monitoringPersons}>
                    <div className={styles.person1}>
                      <div className={styles.personBody}></div>
                      <div className={styles.personHead}></div>
                    </div>
                    <div className={styles.person2}>
                      <div className={styles.personBody}></div>
                      <div className={styles.personHead}></div>
                    </div>
                  </div>
                  <div className={styles.dataElements}>
                    <div className={styles.dataBar1}></div>
                    <div className={styles.dataBar2}></div>
                    <div className={styles.dataBar3}></div>
                    <div className={styles.dataPie}></div>
                  </div>
                  <div className={styles.gearIcon}></div>
                </div>
              </div>
            </div>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Continuous Monitoring of Third Parties
              </h2>
              <div className={styles.titleUnderline}></div>
              <p className={styles.sectionDescription}>
                Cyber threats evolve—and so do your vendors. Virtual IP 
                continuously monitors the security picture of third-party entities to 
                detect newly introduced risks, ensuring you stay protected even 
                after the initial onboarding.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Supply Chain Risk Mitigation Section */}
      <section className={styles.supplyChainSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                End-to-End Supply Chain Risk Mitigation
              </h2>
              <div className={styles.titleUnderline}></div>
              <p className={styles.sectionDescription}>
                From software providers to logistics partners, we assess and 
                secure every point in your supply chain. This prevents indirect 
                vulnerabilities from affecting your business operations, data privacy, 
                or customer trust.
              </p>
            </div>
            <div className={styles.sectionVisual}>
              <div className={styles.supplyChainIllustration}>
                <div className={styles.supplyChainDiagram}>
                  <div className={styles.handPointing}></div>
                  <div className={styles.centralHub}>
                    <span className={styles.hubText}>Supply Chain</span>
                  </div>
                  <div className={styles.chainNode} data-position="top">
                    <span>Retailer</span>
                  </div>
                  <div className={styles.chainNode} data-position="right">
                    <span>Logistics</span>
                  </div>
                  <div className={styles.chainNode} data-position="bottom-right">
                    <span>Consumer</span>
                  </div>
                  <div className={styles.chainNode} data-position="bottom">
                    <span>Supplier</span>
                  </div>
                  <div className={styles.chainNode} data-position="bottom-left">
                    <span>Distributer</span>
                  </div>
                  <div className={styles.chainNode} data-position="left">
                    <span>Manufacturer</span>
                  </div>
                  <div className={styles.connectionLines}>
                    <div className={styles.connectionLine}></div>
                    <div className={styles.connectionLine}></div>
                    <div className={styles.connectionLine}></div>
                    <div className={styles.connectionLine}></div>
                    <div className={styles.connectionLine}></div>
                    <div className={styles.connectionLine}></div>
                  </div>
                  <div className={styles.arrowIndicators}>
                    <div className={styles.arrow}></div>
                    <div className={styles.arrow}></div>
                    <div className={styles.arrow}></div>
                    <div className={styles.arrow}></div>
                    <div className={styles.arrow}></div>
                    <div className={styles.arrow}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regulatory Compliance Section */}
      <section className={styles.complianceSection}>
        <div className={styles.container}>
          <div className={styles.sectionGrid}>
            <div className={styles.sectionVisual}>
              <div className={styles.complianceIllustration}>
                <div className={styles.complianceBackground}>
                  <div className={styles.complianceElements}>
                    <div className={styles.gavelIcon}></div>
                    <div className={styles.rocketIcon}></div>
                    <div className={styles.documentsIcon}></div>
                    <div className={styles.cardIcon}></div>
                    <div className={styles.searchIcon}></div>
                    <div className={styles.chartIcon}></div>
                  </div>
                  <div className={styles.centralFigure}>
                    <div className={styles.personCompliance}>
                      <div className={styles.personBody}></div>
                      <div className={styles.personHead}></div>
                    </div>
                    <div className={styles.complianceBoard}>
                      <div className={styles.boardHeader}>COMPLIANCE</div>
                      <div className={styles.checkItems}>
                        <div className={styles.checkItem}>
                          <div className={styles.checkBox}>✓</div>
                          <div className={styles.checkLine}></div>
                        </div>
                        <div className={styles.checkItem}>
                          <div className={styles.checkBox}>✓</div>
                          <div className={styles.checkLine}></div>
                        </div>
                        <div className={styles.checkItem}>
                          <div className={styles.checkBox}>✓</div>
                          <div className={styles.checkLine}></div>
                        </div>
                        <div className={styles.checkItem}>
                          <div className={styles.checkBox}>✓</div>
                          <div className={styles.checkLine}></div>
                        </div>
                        <div className={styles.checkItem}>
                          <div className={styles.checkBox}>✓</div>
                          <div className={styles.checkLine}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.sectionContent}>
              <h2 className={styles.sectionTitle}>
                Regulatory and Contractual Compliance Support
              </h2>
              <div className={styles.titleUnderline}></div>
              <p className={styles.sectionDescription}>
                Virtual IP helps integrate robust security requirements into vendor 
                contracts and ensures your third-party ecosystem aligns with 
                standards like GDPR, HIPAA, and SOC 2. We help you create 
                enforceable policies that protect your interests legally and 
                operationally.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ThirdPartyRiskPage;